import React, { createContext, useContext, useState, useCallback } from 'react';
import type { Bookmark } from '@/types/api';

interface BookmarkEditContextValue {
  editingBookmark: Bookmark | null;
  isEditModalOpen: boolean;
  openEditModal: (bookmark: Bookmark) => void;
  closeEditModal: () => void;
}

const BookmarkEditContext = createContext<BookmarkEditContextValue | undefined>(
  undefined,
);

export function BookmarkEditContextProvider({ children }: { children: React.ReactNode }) {
  const [editingBookmark, setEditingBookmark] = useState<Bookmark | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const openEditModal = useCallback((bookmark: Bookmark) => {
    setEditingBookmark(bookmark);
    setIsEditModalOpen(true);
  }, []);

  const closeEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    // Keep bookmark data briefly for exit animation
    setTimeout(() => setEditingBookmark(null), 300);
  }, []);

  return (
    <BookmarkEditContext.Provider
      value={{
        editingBookmark,
        isEditModalOpen,
        openEditModal,
        closeEditModal,
      }}
    >
      {children}
    </BookmarkEditContext.Provider>
  );
}

export function useBookmarkEdit() {
  const context = useContext(BookmarkEditContext);
  if (!context) {
    throw new Error('useBookmarkEdit must be used within BookmarkEditContextProvider');
  }
  return context;
}
